/**
 * Slot bookkeeping for the glyph atlas, kept free of the DOM so the overflow rule
 * is unit-testable (`client/test/glyphSlots.test.ts`). `glyphs.ts` owns the canvas.
 *
 * The rule this exists to enforce: **an occupied cell is never rewritten.**
 * `glyphs.ts` used to clamp `nextSlot` to the last cell once the atlas filled, so
 * every further character was drawn over cell 440 and silently corrupted every
 * glyph already cached at that UV. Now the last cell is reserved for a
 * replacement glyph, real characters take cells 0..capacity-2, and overflow
 * characters are drawn as the replacement.
 */

/** What an overflow character is drawn as. */
export const REPLACEMENT_CHAR = '�';

export class SlotAllocator {
  private n = 0;

  /** `capacity` is the TOTAL cell count; one cell is held back for the replacement. */
  constructor(readonly capacity: number) {
    if (!(capacity >= 2)) throw new Error('atlas needs at least two cells');
  }

  /** Cell reserved for the replacement glyph. Never handed out by `alloc()`. */
  get replacementSlot(): number {
    return this.capacity - 1;
  }

  /** Cells available to real characters. */
  get usable(): number {
    return this.capacity - 1;
  }

  get used(): number {
    return this.n;
  }

  /** The next free cell, or null when the atlas is full. Never repeats a cell. */
  alloc(): number | null {
    if (this.n >= this.usable) return null;
    return this.n++;
  }

  reset(): void {
    this.n = 0;
  }
}

/**
 * A Map with a hard ceiling. `set` on a new key beyond the ceiling is refused
 * (returns false); existing keys can always be updated. Used for the glyph cache
 * so arbitrary input characters cannot grow it without bound.
 */
export class BoundedCache<V> {
  private m = new Map<string, V>();

  constructor(readonly max: number) {}

  get size(): number {
    return this.m.size;
  }

  get(key: string): V | undefined {
    return this.m.get(key);
  }

  set(key: string, value: V): boolean {
    if (!this.m.has(key) && this.m.size >= this.max) return false;
    this.m.set(key, value);
    return true;
  }

  clear(): void {
    this.m.clear();
  }
}
